/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty } from 'lodash';
import { IoCloseSharp } from 'react-icons/io5';
import { isKanbanMode } from '../../selectors/teamSelector';
import Dropdown from '../Buttons/dropdown';
import Holon from '../Buttons/holon';
import Estimate from '../Common/estimate';
import ShortNameField from '../Common/shortNameField';
import DescriptionField from '../Common/descriptionField';
import Attachments from '../Common/attachments';
import Owners from '../Common/owners';
import Status from '../Common/status';
import ResultsDropdown from '../Common/resultsDropdown';
import TaskType from './taskType';
import modalTypes from '../../constants/modalTypes';
import IntentionTypes from '../../constants/intentionTypes';
import { taskId } from '../../utils/string';
import { openModal } from '../../actions/modalActions';
import {
  updateTask,
  deleteTask,
  addTaskOwner,
  editTaskOwner,
  removeTaskOwner,
  createTask,
  changeTaskToStory,
  restoreTask
} from '../../actions/taskActions';
import { allows } from '../../utils/authorization';
import { getTaskIntention, scrumSprint } from '../../utils/taskHelpers';

/* eslint-disable jsx-a11y/label-has-associated-control */
class TaskDetail extends React.Component {
  constructor(props) {
    super(props);
    const {
      task: prevTask,
      teamMemberWall,
      newMenu,
      taskTypes,
      isKanban,
      currentTask,
      owner,
      teams,
      workPackage: taskWorkPackage,
      selectedSpace
    } = props;

    const taskWorkPackageId =
      prevTask?.work_package_id || taskWorkPackage?.id || null;

    const workPackage =
      Object.entries(props.workPackage).length > 0
        ? props.workPackage
        : props.workPackages.all.find((wp) => wp.id === taskWorkPackageId) ||
          {};

    const taskTeam = teams.find(
      (team) =>
        team.id === workPackage.team_id ||
        (team.id === selectedSpace.id &&
          selectedSpace.type === 'team' &&
          teamMemberWall)
    );

    const teamMembers = taskTeam
      ? taskTeam.members.filter((member) => {
          if (
            member.team_roles.find(
              (role) => role.team_id === taskTeam.id && role.type === 'Worker'
            )
          ) {
            return true;
          }
          return false;
        })
      : [];

    const teamMemberIds = teamMembers.map((tm) => tm.id);

    const defaultOwnerId = teamMemberIds.includes(
      owner?.id || taskWorkPackage.owner_id
    )
      ? [owner?.id || taskWorkPackage.owner_id]
      : [];

    const task =
      currentTask && (currentTask.id || currentTask.temporaryId)
        ? currentTask
        : {
            ...prevTask,
            work_package_id: workPackage.id,
            blocked: false,
            description: '',
            task_owner_ids:
              prevTask && prevTask.task_owner_ids
                ? prevTask.task_owner_ids
                : defaultOwnerId
          };

    const intention =
      scrumSprint(workPackage, isKanban) && !workPackage.completed
        ? IntentionTypes.DEVELOPMENT
        : getTaskIntention(workPackage.state);

    const taskType = taskTypes.find(
      (taskType) => taskType.id === task?.task_type_id
    );
    if (taskType) {
      const { intention: taskIntention, kind } = taskType;
      task.task_type = { kind, intention: taskIntention };
    } else if (!task.taskType)
      task.task_type = {
        kind: this.getTaskKind(intention),
        intention
      };

    this.state = {
      task,
      workPackage,
      teamMembers,
      attachmentList: task.attachments || [],
      inputEdited: false,
      workPackageError: false,
      isFull: false,
      errors: {
        short_name: ''
      },
      selectedState:
        teamMemberWall || newMenu ? 'Getting Ready' : workPackage.state || ''
    };
  }

  getTaskKind(intention) {
    switch (intention) {
      case IntentionTypes.DEVELOPMENT:
        return 'Coding';
      case IntentionTypes.GETTING_READY:
        return 'Analysis';
      case IntentionTypes.ACCEPTING:
        return 'Test Analysis';
      case IntentionTypes.SHIPPING:
        return 'Deployment Planning';
      case IntentionTypes.DEPLOYING:
        return 'Deploying';
      default:
        return 'Analysis';
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.isSending &&
      !nextProps.isSending &&
      nextProps.currentTask &&
      nextProps.currentTask.id
    ) {
      this.setState({ task: { ...nextProps.currentTask } });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.workPackage.id !== prevState.workPackage.id) {
      const taskTeam = this.props.teams.find(
        (team) => team.id === this.state.workPackage.team_id
      );
      const teamMembers = taskTeam
        ? taskTeam.members.filter((member) => {
            if (
              member.team_roles.find(
                (role) => role.team_id === taskTeam.id && role.type === 'Worker'
              )
            ) {
              return true;
            }
            return false;
          })
        : [];

      const teamMemberIds = teamMembers.map((tm) => tm.id);

      const newOwnerIds = this.state.task.task_owner_ids.filter((ownerId) => {
        return teamMemberIds.includes(ownerId);
      });

      this.setState((prevState) => ({
        ...prevState,
        teamMembers,
        task: { ...prevState.task, task_owner_ids: newOwnerIds }
      }));
    }
  }

  handleOwnerAdd = (taskID, ownerId) => {
    const { task } = this.state;
    const newTask = cloneDeep(task);

    const idExist =
      newTask.task_owner_ids.findIndex((id) => id === ownerId) > -1;
    if (!idExist) newTask.task_owner_ids.unshift(ownerId);
    if (task.id) this.props.addTaskOwner(taskID, ownerId);
    this.setState({ task: newTask });
  };

  handleOwnerEdit = (...args) => {
    const { task } = this.state;
    const newTask = cloneDeep(task);

    const idExist =
      newTask.task_owner_ids.findIndex((id) => id === args[2]) > -1;
    if (!idExist) {
      newTask.task_owner_ids = newTask.task_owner_ids.filter(
        (id) => id !== args[1]
      );
      newTask.task_owner_ids.push(args[2]);
    }
    if (newTask.id && args.length > 2)
      this.props.editTaskOwner(args[0], args[1], args[2]);
    this.setState({ task: newTask });
  };

  handleOwnerRemove = (taskID, ownerId) => {
    const { task } = this.state;
    const newTask = cloneDeep(task);

    newTask.task_owner_ids = newTask.task_owner_ids.filter(
      (id) => id !== ownerId
    );
    if (newTask.id) this.props.removeTaskOwner(taskID, ownerId);
    this.setState({ task: newTask });
  };

  handleKeyPress = (event) => {
    const { key, target } = event;
    const { task } = this.state;
    const newTask = cloneDeep(task);

    if (key === 'Enter' && !newTask.id) {
      newTask[target.name] = target.value;
      this.setState({ task: newTask }, () => this.createNewTask(event));
    }
  };

  handleFieldChange = (field) => {
    const { task, workPackageError } = this.state;
    const {
      workPackages: { stories }
    } = this.props;
    if (workPackageError && field.work_package_id) {
      this.setState({ workPackageError: false });
    }
    if (field.short_name && field.short_name === task.short_name) return;
    const allowEdit = allows('update', task, 'task');
    let newTask = cloneDeep(task);
    if (task.id && allowEdit) {
      let value = field;
      if (field.target) {
        value = { [field.target.name]: field.target.value };
        newTask[field.target.name] = field.target.value;
      }

      this.props.updateTask(task.id, value);
    }

    if (field.target) newTask[field.target.name] = field.target.value;
    else if (field.task_type) {
      newTask.task_type = { ...field.task_type };
      this.setState({ selectedState: field.state });
    } else newTask = { ...task, ...field };

    if (field.work_package_id) {
      const selectedResult = stories.find(
        (s) => s.id === field.work_package_id
      );
      this.setState({ task: newTask, workPackage: selectedResult });
      return;
    }
    this.setState({ task: newTask });
  };

  handleTaskBlock = (blockStatus) => {
    const payload = { blocked: blockStatus };
    this.handleFieldChange(payload);
  };

  handleTaskDelete = () => {
    this.props.deleteTask(this.props.currentTask);
    this.props.onClose();
  };

  handleTaskRestore = () => {
    this.props.restoreTask(this.props.currentTask.id);
    this.props.onClose();
  };

  openParent = () => {
    const { task } = this.state;
    if (task.work_package_id) {
      this.props.openModal(modalTypes.resultDetail, {
        id: task.work_package_id
      });
    }
  };

  addAttachment = (attachment) => {
    const { attachmentList } = this.state;
    this.setState({ attachmentList: attachmentList.concat(attachment) });
  };

  removeAttachment = (attachmentIndex) => {
    const { attachmentList } = this.state;
    const newAttachmentList = attachmentList.filter(
      (att, index) => index !== attachmentIndex
    );
    this.setState({ attachmentList: newAttachmentList });
  };

  createNewTask = (event) => {
    event.preventDefault();
    const { task, attachmentList, errors } = this.state;
    const newTask = cloneDeep(task);

    if (newTask.taskType) newTask.task_type = newTask.taskType;

    if (newTask.short_name) {
      const { workPackage } = this.props;
      newTask.cadence_id = null;
      if (newTask.work_package_id > 0) {
        // cadence Ids is an array of integers
        if (!isEmpty(workPackage.cadence_ids)) {
          newTask.cadence_id =
            workPackage.cadence_ids[workPackage.cadence_ids.length - 1];
        }
        this.props.createTask(newTask, attachmentList);
        this.props.onClose();
      } else {
        // cadence Id is an integer
        if (workPackage.cadence_id) {
          newTask.cadence_ids = workPackage.cadence_id;
        }
        if (this.props.createTemporaryTask) {
          this.props.createTemporaryTask(newTask, attachmentList);
          this.props.onClose();
        }
        this.setState({ workPackageError: true });
      }
    } else {
      errors.short_name = "Can't be blank";
      this.setState({ inputEdited: true, errors });
    }
  };

  changeToStory = () => {
    this.props.changeTaskToStory(this.state.task);
  };

  toggleIsFull = () =>
    this.setState((prevState) => ({ isFull: !prevState.isFull }));

  generateTaskIntention = () => {
    const { task } = this.state;
    const { teamMemberWall } = this.props;

    if (teamMemberWall) {
      return "In Progress";
    }

    return task.task_type && task.task_type.intention
  }

  generateTaskKind = () => {
    const { task } = this.state;
    const { teamMemberWall } = this.props;

    if (teamMemberWall) {
      return "Fixing";
    }

    return task.task_type && task.task_type.kind;
  }

  render() {
    if (!this.props.isOpen) {
      return null;
    }
    const {
      task,
      inputEdited,
      attachmentList,
      isFull,
      errors,
      workPackage,
      teamMembers
    } = this.state;
    const {
      teamMemberWall,
      newMenu,
      isSending,
      fromResultDetail,
      isKanban,
      selectedSpace
    } = this.props;

    const isNew = !task.id;
    const allowEdit = task.id ? allows('update', task, 'task') : true;
    const allowDelete = task.id ? allows('destroy', task, 'task') : true;
    const allowRestore = task.id ? allows('restore_tasks', task, 'task') : true;
    const owners =
      task.task_owner_ids && task.task_owner_ids.length > 0
        ? task.task_owner_ids.reduce((acc, id) => {
            const member = teamMembers.find((usr) => usr.id === id);
            if (member) {
              acc.push(member);
            }
            return acc;
          }, [])
        : [];

    return (
      <div
        id={`task-detail-${task.id || 'new'}`}
        className="detail-modal task-modal"
        tabIndex="-1"
        role="dialog"
      >
        <div className="detail-dialog task-detail-card" role="document">
          {isSending ? <div id="loader" /> : null}
          <div className={`detail-content ${isSending ? 'blur-loading' : ''}`}>
            <div className="modal-header detail-header">
              <div>
                <div className="col-sm-4">
                  <Dropdown
                    blocked={task.blocked}
                    handleBlock={this.handleTaskBlock}
                    handleDelete={this.handleTaskDelete}
                    handleRestore={this.handleTaskRestore}
                    changeToStory={this.changeToStory}
                    allowDelete={isNew ? false : allowDelete}
                    allowEdit={isNew ? false : allowEdit}
                    allowRestore={isNew ? false : allowRestore}
                    type="Task"
                    deleted={task.deleted_at}
                  />
                  <strong
                    className={`modal-title ${task.blocked ? 'red' : ''}`}
                  >
                    {isNew ? 'New Task' : taskId(task, workPackage.type_id)}
                  </strong>
                </div>
                <div className="col-sm-4 text-center">
                  <Holon action={this.openParent} />
                </div>
                <div className="col-sm-4 form-inline text-center">
                  <div>
                    <div className="col-sm-10 estimate-field">
                      <Estimate
                        estimate={task.estimate}
                        onFieldChange={this.handleFieldChange}
                        label
                        allowEdit={allowEdit}
                      />
                    </div>
                    <div className="col-sm-offset-1 col-sm-1 detail-close">
                      <button
                        type="button"
                        className="btn-close"
                        onClick={this.props.onClose}
                      >
                        <IoCloseSharp size="2em" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  <ShortNameField
                    autoFocus
                    shortName={task.short_name}
                    inputEdited={inputEdited}
                    onNameChange={this.handleFieldChange}
                    allowEdit={allowEdit}
                    onKeyUp={this.handleKeyPress}
                  />
                </div>
                {errors.short_name && (
                  <p className="red"> {errors.short_name} </p>
                )}
                <div
                  className={`col-sm-${
                    isFull ? 12 : 7
                  } task-description-container ${
                    teamMemberWall || newMenu ? '' : 'without-results'
                  }`}
                >
                  <DescriptionField
                    id={task.id}
                    type="Task"
                    description={task.description}
                    toggleIsFull={this.toggleIsFull}
                    onFieldChange={this.handleFieldChange}
                    allowEdit={allowEdit}
                    isFull={isFull}
                  />
                </div>
                {!isFull && (
                  <div className="col-sm-5">
                    <TaskType
                      isNew={isNew}
                      fromResultDetail={fromResultDetail}
                      teamMemberWall={teamMemberWall}
                      parent={workPackage}
                      kind={this.generateTaskKind()}
                      intention={this.generateTaskIntention()}
                      onFieldChange={this.handleFieldChange}
                      disabled={!allowEdit}
                      isKanban={isKanban}
                    />
                    {(teamMemberWall || newMenu) && (
                      <>
                        <br />
                        <ResultsDropdown
                          onFieldChange={this.handleFieldChange}
                          workPackagesReducer={this.props.workPackages}
                          workPackageError={this.state.workPackageError}
                          selectedState={this.state.selectedState}
                          task={this.state.task}
                          allowEdit
                        />
                      </>
                    )}
                    <br />
                    <Attachments
                      itemId={task.id}
                      type="task"
                      allowCreate={
                        isNew
                          ? true
                          : allows('create_attachments', task, 'task')
                      }
                      allowDelete={
                        isNew
                          ? true
                          : allows('destroy_attachments', task, 'task')
                      }
                      attachmentList={attachmentList}
                      addAttachment={this.addAttachment}
                      removeAttachment={this.removeAttachment}
                      isNew={!task.id}
                    />
                    <br />
                    <Owners
                      taskId={task.id || ''}
                      owners={owners || []}
                      ownerIds={task.task_owner_ids}
                      users={teamMembers}
                      handleOwnerAddition={this.handleOwnerAdd}
                      handleOwnerEdit={this.handleOwnerEdit}
                      handleOwnerRemoval={this.handleOwnerRemove}
                      allowEdit={allowEdit}
                    />
                  </div>
                )}

                <div className="col-sm-12" style={{ marginTop: '28px' }}>
                  {!task.id && (
                    <div className="save-cancel-buttons">
                      <label>&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-md button-lavender"
                        onClick={this.createNewTask}
                        disabled={isSending}
                      >
                        <strong>save</strong>
                      </button>
                      <button
                        style={{ marginLeft: '5px' }}
                        type="button"
                        className="btn btn-md btn-default button-normal"
                        onClick={this.props.onClose}
                      >
                        <strong>Cancel</strong>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Status
                task={task}
                started={task.started}
                completed={task.completed}
                onStatusChange={this.handleFieldChange}
                allowEdit={allowEdit}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TaskDetail.propTypes = {
  task: PropTypes.object,
  currentTask: PropTypes.object,
  user: PropTypes.object,
  workPackage: PropTypes.object,
  types: PropTypes.array,
  intentions: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  openModal: PropTypes.func,
  deleteTask: PropTypes.func,
  updateTask: PropTypes.func,
  addTaskOwner: PropTypes.func,
  editTaskOwner: PropTypes.func,
  removeTaskOwner: PropTypes.func
};

TaskDetail.defaultProps = {
  workPackage: {}
};

const mapStateToProps = (state, ownProps) => ({
  user: state.userReducer.user,
  teams: state.teamReducer.teams,
  intentions:
    state.defaultOptionsReducer.defaultOptions.task_intention_defaults || [],
  types: state.defaultOptionsReducer.defaultOptions.task_type_defaults || [],
  currentTask: ownProps.task
    ? state.taskReducer.tasks.find((task) => task.id === ownProps.task.id)
    : {},
  workPackages: state.workPackageReducer,
  isSending: state.taskReducer.isSending,
  taskTypes: state.taskTypeReducer.taskTypes,
  isKanban: isKanbanMode(state) || false,
  selectedSpace: state.workPackageReducer.selectedSpace
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      openModal,
      updateTask,
      deleteTask,
      addTaskOwner,
      editTaskOwner,
      removeTaskOwner,
      createTask,
      changeTaskToStory,
      restoreTask
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TaskDetail);
